import { useEffect, useRef, useState } from 'react';
import {
  Box,
  Card,
  Table,
  Button,
  TableBody,
  Container,
  TableContainer,
  TablePagination,
  TableHead,
  TableRow,
  TableCell,
  IconButton,
  Avatar,
  Chip,
  Stack,
} from '@mui/material';
import { Link, Link as RouterLink } from 'react-router-dom';
import Page from '../../components/Page';
import Iconify from '../../components/Iconify';
import Scrollbar from '../../components/Scrollbar';
import Label from '../../components/Label';
import Searchbar from '../../components/Searchbar';
import { useAppDispatch, useAppSelector } from '../../store';
import { findPropertyFeature } from '../../store/propertyFeature/api';
import {
  setCurrentPage,
  setLimit,
  setSkip,
} from '../../store/propertyFeature/propertyFeatureSlice';
import Loader from '../../components/Loader';
import {
  reset as resetPropertyFeature,
  setStatus as setPropertyFeatureStatus,
} from '../../store/propertyFeature/propertyFeatureSlice';
import { encryptString, getAssetURl } from '../../utils/url';
import CloseIcon from '@mui/icons-material/Close';
import {
  PropertyFeatureMapper,
  PropertySubFeatureMapper,
} from '../../store/propertyFeature/api/interface';

const Index = () => {
  const dispatch = useAppDispatch();
  const { data, totalRecords, limit, currentPage, status, skip, isLoading } =
    useAppSelector((state) => state.propertyFeature);
  const [search, setSearch] = useState<string>('');

  useEffect(() => {
    if (status === 'idle') {
      dispatch(
        findPropertyFeature({
          limit,
          skip,
          ...(search && { search: search }),
        })
      );
    }
  }, [dispatch, limit, skip, status]);

  useEffect(() => {
    dispatch(resetPropertyFeature());
  }, [search]);

  const handlePageChange = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    newPage: number
  ): void => {
    dispatch(setSkip({ skip: limit * newPage }));
    dispatch(setCurrentPage({ currentPage: newPage }));
    dispatch(setPropertyFeatureStatus({ status: 'idle' }));
  };
  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    dispatch(setLimit({ limit: +event.target.value }));
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setSearch(e.target.value);
  };

  const boxRef = useRef<HTMLDivElement>(null);
  const [openLightBox, setOpenLightBox] = useState(false);
  const [imageState, setImageState] = useState<string>();

  const handleOpenLightBox = (imageObject: string) => {
    setOpenLightBox(true);
    setImageState(imageObject);
  };

  const handleCloseLightBox = () => {
    setOpenLightBox(false);
    setImageState('');
  };

  return (
    <Page title='Property Feature: List'>
      <Container maxWidth='xl'>
        <Stack
          direction='row'
          alignItems='center'
          justifyContent='flex-end'
          spacing={{ xs: 0.5, sm: 1.5 }}
          my={1}
        >
          <Button
            variant='contained'
            component={RouterLink}
            to={'/property-feature/create'}
            startIcon={<Iconify icon={'eva:plus-fill'} />}
          >
            {'New Property Feature'}
          </Button>
        </Stack>

        <Card>
          <Searchbar handleSearch={handleSearch} />
          <Scrollbar>
            <TableContainer sx={{ minWidth: 1000, position: 'relative' }}>
              <Table size={'medium'}>
                <TableHead>
                  <TableRow>
                    <TableCell align='center'>{'Icon'}</TableCell>
                    <TableCell align='center'>{'Category'}</TableCell>
                    <TableCell align='center'>{'Sub Category'}</TableCell>
                    <TableCell align='center'>{'Position'}</TableCell>
                    <TableCell align='center'>{'Label EN'}</TableCell>
                    <TableCell align='center'>{'Label AR'}</TableCell>
                    <TableCell align='center'>{'Status'}</TableCell>
                    <TableCell align='center'>{'Action'}</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {isLoading ? (
                    <TableRow hover>
                      <TableCell
                        colSpan={8}
                        align='center'
                      >
                        <Loader />
                      </TableCell>
                    </TableRow>
                  ) : data?.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={8}
                        align='center'
                      >
                        {'No record found'}
                      </TableCell>
                    </TableRow>
                  ) : (
                    data?.map((propertyFeature) => (
                      <TableRow key={propertyFeature.id}>
                        <TableCell
                          align='center'
                          sx={{
                            display: 'table-cell',
                            justifyContent: 'center',
                          }}
                        >
                          {propertyFeature?.icon && (
                            <Avatar
                              onClick={() =>
                                handleOpenLightBox(propertyFeature?.icon)
                              }
                              alt={propertyFeature?.icon}
                              imgProps={{ crossOrigin: 'anonymous' }}
                              src={getAssetURl(propertyFeature?.icon)}
                              sx={{
                                borderRadius: 'unset',
                                m: 1,
                                objectFit: 'contain',
                                '& img': { objectFit: 'contain' },
                              }}
                            />
                          )}
                        </TableCell>
                        <TableCell align='center'>
                          {propertyFeature?.propertyFeatureMapper?.map(
                            (
                              propertyFeatureMapperItem: PropertyFeatureMapper
                            ) => {
                              return propertyFeatureMapperItem
                                ?.property_category?.labelEn ? (
                                <Box>
                                  <Chip
                                    label={
                                      propertyFeatureMapperItem
                                        ?.property_category?.labelEn
                                    }
                                    sx={{ m: 0.2, fontWeight: 600 }}
                                  />
                                </Box>
                              ) : (
                                <></>
                              );
                            }
                          )}
                        </TableCell>
                        <TableCell align='center'>
                          {propertyFeature?.propertyFeatureSubCategoryMapper?.map(
                            (
                              propertySubFeatureMapperItem: PropertySubFeatureMapper
                            ) => {
                              return propertySubFeatureMapperItem
                                ?.property_sub_category?.labelEn ? (
                                <Box>
                                  <Chip
                                    label={
                                      propertySubFeatureMapperItem
                                        ?.property_sub_category?.labelEn
                                    }
                                    sx={{ m: 0.2, fontWeight: 600 }}
                                  />
                                </Box>
                              ) : (
                                <></>
                              );
                            }
                          )}
                        </TableCell>
                        <TableCell align='center'>
                          {propertyFeature?.position}
                        </TableCell>
                        <TableCell align='center'>
                          {propertyFeature?.labelEn}
                        </TableCell>
                        <TableCell align='center'>
                          {propertyFeature?.labelAr}
                        </TableCell>
                        <TableCell align='center'>
                          <Label
                            variant='ghost'
                            color={
                              propertyFeature?.activeState ? 'success' : 'error'
                            }
                          >
                            {propertyFeature?.activeState
                              ? 'Active'
                              : 'InActive'}
                          </Label>
                        </TableCell>
                        <TableCell align='center'>
                          <IconButton aria-label='visibility'>
                            <Link
                              to={`/property-feature/edit/${encryptString(
                                propertyFeature?.id ?? ''
                              )}`}
                              state={{ ...propertyFeature }}
                              style={{ color: '#808080' }}
                            >
                              <Iconify icon={'eva:edit-fill'} />
                            </Link>
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Scrollbar>

          <Box sx={{ position: 'relative' }}>
            <TablePagination
              rowsPerPageOptions={[25, 50, 100]}
              component='div'
              count={totalRecords}
              rowsPerPage={limit}
              page={Math.min(Math.round(totalRecords / limit), currentPage)}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleRowsPerPageChange}
            />
          </Box>
        </Card>
      </Container>
      {openLightBox && (
        <Box
          sx={{ pt: 'env(safe-area-inset-top) !important' }}
          id='myModal'
          className='modal'
        >
          <span className='close cursor'>
            <CloseIcon
              fontSize='large'
              onClick={handleCloseLightBox}
              sx={{ mt: 'env(safe-area-inset-top) !important' }}
            />
          </span>
          <div
            className={`modal-content ${
              openLightBox ? 'display-block ' : 'display-none'
            }`}
          >
            {imageState && (
              <div className='vertical-center-div'>
                <div
                  ref={boxRef}
                  className={`mySlides ${
                    openLightBox ? 'display-block ' : 'display-none'
                  }`}
                >
                  <img
                    id={imageState}
                    src={getAssetURl(imageState)}
                    width='100%'
                    height='100%'
                    crossOrigin='anonymous'
                    alt={imageState}
                  />
                </div>
              </div>
            )}
          </div>
        </Box>
      )}
    </Page>
  );
};

export default Index;
